import React from 'react';
import { StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';

import type { TabItems } from '../../Tabs.types';
import { Tab } from '../Tab';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders the controlled "tablist" component along with the relevant "tab"
 * components to change between tab panels.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/tablist_role ARIA: tablist role}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/tab_role ARIA: tab role}
 */
export const TabBar = (props: TabBarProps) => {
  const { tabs, activeTabId, onChange } = props;

  return (
    <View style={styles.tabBar} accessibilityRole="tablist">
      {tabs.map((tab) => {
        const { id, panelId, label, isDisabled } = tab;

        return (
          <Tab
            key={id}
            id={id}
            panelId={panelId}
            label={label}
            isActive={activeTabId === id}
            isDisabled={isDisabled}
            onPress={onChange}
          />
        );
      })}
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  tabBar: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.GRAY,
    paddingHorizontal: theme.spacing['1'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type TabBarProps = Readonly<{
  tabs: TabItems;
  activeTabId: string;
  onChange: (value: string) => void;
}>;
